/**
 * "Add usual safety equipment" — the NHRA starter sheet (see safety-kit.ts).
 *
 * Opened from a car or a driver. It asks for the machine's ET and trap speed,
 * pre-checks the gear the NHRA brackets call for at that pace, and adds the ones
 * the racer confirms as safety items. It is explicit about being a starting
 * point, not the rulebook, and links to a certified chassis inspector — because
 * getting this wrong is not a broken screen, it is a racer who thinks they are
 * covered when they are not.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, ShieldCheck, TriangleAlert } from 'lucide-react';

import type { OwnerType } from '@svcdue/contracts';

import { useCreateItems } from '../api/hooks';
import { errorMessage } from '../components/states';
import { Field, SheetHead, TrigToggle } from '../components/form';
import { NHRA_INSPECTOR_URL, recommended, rulesForOwner, toItemRequest } from './safety-kit';
import type { SafetyOwner } from './safety-kit';

export function SafetyKit({
  garageId,
  owner,
  onClose,
}: {
  garageId: string;
  owner: SafetyOwner;
  onClose: () => void;
}) {
  const ownerType: OwnerType = owner.ownerType;
  const rules = useMemo(() => rulesForOwner(ownerType), [ownerType]);

  const [etText, setEtText] = useState('');
  const [mphText, setMphText] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Which items the racer has hand-toggled — so re-recommending on a pace change
  // never silently undoes their choices.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const etNum = Number.parseFloat(etText);
  const mphNum = Number.parseFloat(mphText);
  // A pace has to be positive to mean anything. Without this a stray "-5" or "0"
  // passes Number.isFinite and pre-checks the entire top-tier kit off nonsense.
  const et = Number.isFinite(etNum) && etNum > 0 ? etNum : null;
  const mph = Number.isFinite(mphNum) && mphNum > 0 ? mphNum : null;

  // Entering (or changing) the pace re-picks the recommended set — but only for
  // items the racer hasn't touched. Editing the trap speed after hand-unchecking
  // the window net shouldn't quietly put it back.
  useEffect(() => {
    const rec = recommended(ownerType, et, mph);
    setSelected((prev) =>
      Object.fromEntries(rules.map((r) => [r.key, touched[r.key] ? Boolean(prev[r.key]) : rec.has(r.key)])),
    );
  }, [et, mph, ownerType, rules, touched]);

  const create = useCreateItems(garageId);
  const chosen = rules.filter((r) => selected[r.key]);
  const hasPace = et !== null || mph !== null;
  const busy = create.isPending;
  // A synchronous latch: two clicks (or an Enter auto-repeat) dispatched before
  // React re-renders and disables the button would otherwise both fire the create.
  const submitting = useRef(false);

  const toggle = (key: string) => {
    setTouched((t) => ({ ...t, [key]: true }));
    setSelected((s) => ({ ...s, [key]: !s[key] }));
  };

  const add = async () => {
    if (chosen.length === 0 || submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      // One request, one transaction: the API creates the whole kit atomically,
      // so there is no partial state and a retry can't duplicate what succeeded.
      await create.mutateAsync({ items: chosen.map((r) => toItemRequest(r, owner)) });
      onClose();
    } catch (e) {
      // Nothing was created (atomic), so the racer can simply try again.
      setError(errorMessage(e));
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className="sheet">
      <SheetHead
        title="Add safety equipment"
        sub={owner.label}
        onClose={onClose}
        saveLabel={busy ? 'Adding…' : chosen.length > 0 ? `Add ${chosen.length}` : 'Add'}
        onSave={add}
        saveDisabled={chosen.length === 0 || busy}
      />
      <div className="sheet-body">
        <div className="form-sec">
          <ShieldCheck size={12} /> Your pace
        </div>
        <p className="safety-intro">
          Enter the machine's best <b className="c-ink">quarter-mile ET</b> and <b className="c-ink">trap speed</b>, and
          we'll pre-check the gear the NHRA brackets call for. Run eighth-mile? Use your projected quarter-mile ET.
        </p>
        <div className="field-grid-2">
          <Field label="Elapsed time" hint="1/4-mile seconds">
            <input
              className="input"
              value={etText}
              onChange={(e) => setEtText(e.target.value)}
              inputMode="decimal"
              placeholder="e.g. 10.50"
              autoFocus
            />
          </Field>
          <Field label="Trap speed" hint="mph">
            <input
              className="input"
              value={mphText}
              onChange={(e) => setMphText(e.target.value)}
              inputMode="decimal"
              placeholder="e.g. 130"
            />
          </Field>
        </div>

        <div className="form-sec">
          Equipment {hasPace && <span className="sec-hint">pre-checked for your pace</span>}
        </div>
        {!hasPace && (
          <p className="safety-intro">Enter your ET or trap speed above to pre-select — or pick from the list.</p>
        )}

        <div className="safety-list">
          {rules.map((rule) => (
            <TrigToggle
              key={rule.key}
              icon={ShieldCheck}
              name={rule.name}
              sub={`${rule.spec} · ${rule.why}`}
              on={Boolean(selected[rule.key])}
              onToggle={() => toggle(rule.key)}
              noInput
            />
          ))}
        </div>

        {/* The load-bearing disclaimer. This feature is a convenience; it must not
            read as authoritative, because a racer could be hurt trusting it. */}
        <div className="safety-note">
          <TriangleAlert size={15} />
          <div>
            <b>A starting point, not the rulebook.</b>
            <span>
              Actual requirements depend on your class, body style and sanctioning body, and the NHRA rulebook is
              revised every year. Treat this as a checklist to build on — set each item's real spec and cert date after
              adding it, and confirm the full list with tech.
            </span>
            <a className="safety-link" href={NHRA_INSPECTOR_URL} target="_blank" rel="noreferrer noopener">
              Find an NHRA chassis inspector <ExternalLink size={12} />
            </a>
          </div>
        </div>

        {error && <div className="field-err">{error}</div>}

        <button className="sheet-save-lg" onClick={add} disabled={chosen.length === 0 || busy}>
          {busy ? 'Adding…' : chosen.length > 0 ? `Add ${chosen.length} to ${owner.label}` : 'Select equipment to add'}
        </button>
      </div>
    </div>
  );
}
